import DatePicker from "../../components/common/DatePicker";
import Select from "../../components/common/Select";
import { useEffect, useRef } from "react";

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Success", value: "success" },
  { label: "Pending", value: "pending" },
  { label: "Failed", value: "failed" },
];

import { useState } from "react";

export default function FilterBar({
  searchText,
  setSearchText,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  appliedStatus,
  setAppliedStatus,
  setPage,
  handleExport,
  exporting,
  dateError,
  validateDates,
  serviceOptions = [],
  selectedService,
  setSelectedService,
}) {
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowFormatMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div className="mt-6 mb-6 rounded-[16px] border border-[#CEC3D01A] bg-[#F3F4F5] px-6 py-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        
        {/* LEFT SECTION */}
        <div className="flex flex-wrap items-center gap-4">

          {/* SEARCH */}
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2"
              width="13"
              height="13"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#9ca3af"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path
                d="M21 21l-4.35-4.35"
                strokeLinecap="round"
              />
            </svg>

            <input
              type="text"
              placeholder="Search by transaction ID..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
              className="w-52 rounded-[12px] border-none bg-white py-2.5 pl-8 pr-4 text-[13px] text-[#191C1D] outline-none"
            />
          </div>

          {/* FROM DATE */}
          <div
            className={`flex items-center gap-3 rounded-[12px] bg-white px-4 py-2 ring-1 ${
              dateError
                ? "ring-red-400"
                : "ring-transparent"
            }`}
          >
            <span className="text-[12px] font-semibold uppercase text-[#4C444E]">
              FROM
            </span>

            <DatePicker
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                validateDates(
                  e.target.value,
                  toDate
                );
                setPage(1);
              }}
              className="w-[110px]"
            />
          </div>

          {/* TO DATE */}
          <div
            className={`flex items-center gap-3 rounded-[12px] bg-white px-4 py-2 ring-1 ${
              dateError
                ? "ring-red-400"
                : "ring-transparent"
            }`}
          >
            <span className="text-[12px] font-semibold uppercase text-[#4C444E]">
              TO
            </span>

            <DatePicker
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                validateDates(
                  fromDate,
                  e.target.value
                );
                setPage(1);
              }}
              className="w-[110px]"
            />
          </div>

          {/* SERVICE */}
          <div className="flex items-center gap-3 rounded-[12px] bg-white px-4 py-2 min-w-[190px]">
            <span className="text-[12px] font-semibold uppercase text-[#4C444E]">
              SERVICE:
            </span>

            <Select
              value={selectedService}
              onChange={(e) => {
                setSelectedService(
                  e.target.value
                );
                setPage(1);
              }}
              className="min-w-[80px]"
              options={[
                { label: "All", value: "" },
                ...serviceOptions,
              ]}
            />
          </div>

          {/* ERROR MESSAGE */}
          {dateError && (
            <p className="text-[11px] font-medium text-red-500">
              {dateError}
            </p>
          )}
        </div>

        {/* RIGHT SECTION */}
        <div className="flex flex-wrap items-center gap-3">

          {/* STATUS */}
          <div className="flex items-center gap-3 rounded-[12px] bg-white px-4 py-2 min-w-[80px]">
            <span className="text-[12px] font-semibold uppercase text-[#4C444E]">
              STATUS:
            </span>

            <Select
              value={appliedStatus}
              onChange={(e) => {
                setAppliedStatus(
                  e.target.value
                );
                setPage(1);
              }}
              className="min-w-[110px]"
              options={STATUS_OPTIONS}
            />
          </div>

          {/* EXPORT SPLIT BUTTON */}
          <div className="relative" ref={menuRef}>
            <div className={`flex items-center rounded-lg overflow-hidden ${exporting ? "opacity-60 pointer-events-none" : ""}`}>
              <button
                onClick={() => handleExport("csv")}
                disabled={exporting}
                className="flex items-center gap-2 bg-[#4B2364] hover:bg-[#3a1a4f] px-4 py-[9px] text-[13px] font-semibold text-white cursor-pointer border-none transition-colors"
              >
                {exporting ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                )}
                {exporting ? "Exporting..." : "Export"}
              </button>
              <button
                onClick={() => setShowFormatMenu((v) => !v)}
                disabled={exporting}
                className="flex items-center justify-center bg-[#4B2364] hover:bg-[#3a1a4f] px-2.5 h-[37px] text-white border-none cursor-pointer transition-colors"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`transition-transform duration-200 ${showFormatMenu ? "rotate-180" : ""}`}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>

            {showFormatMenu && (
              <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden w-[160px]">
                <div className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                  Choose Format
                </div>
                <button
                  onClick={() => { handleExport("csv"); setShowFormatMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                >
                  <span className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14,2 14,8 20,8" />
                      <line x1="8" y1="13" x2="16" y2="13" />
                      <line x1="8" y1="17" x2="16" y2="17" />
                    </svg>
                  </span>
                  CSV
                </button>
                <button
                  onClick={() => { handleExport("excel"); setShowFormatMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                >
                  <span className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14,2 14,8 20,8" />
                      <rect x="8" y="12" width="8" height="6" rx="1" />
                    </svg>
                  </span>
                  Excel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}